import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Bounds } from "@/lib/geo";
import type { UnifiedHotelResponse } from "@/api/generated/schemas";

export interface SearchFilters {
  star_rating?: number[];
  price_min?: number;
  price_max?: number;
  board_basis?: string[];
  hotel_name?: string;
  /** Toggle "Rimborsabili" — applicato client-side (BE non lo supporta) */
  refundable_only?: boolean;
}

/**
 * Modalità sort UI. `"smart"` è client-side (vedi `lib/smartSort.ts`) e
 * non viene inviato al BE; gli altri valori si mappano al payload BE
 * `sort.field`.
 */
export type SortMode = "smart" | "price-asc" | "price-desc" | "stars-desc";

/**
 * Sort BE-compatibile (legacy). Mantenuto per retro-compat con
 * FiltersSidebar attuale; sarà rimosso quando ResultsClient è
 * riscritta e FiltersSidebar cancellata.
 */
export interface SearchSort {
  field: "price" | "rating" | "distance";
  order: "asc" | "desc";
}

/** Vista risultati: lista, mappa, oppure split (default desktop). */
export type ViewMode = "list" | "map" | "split";

export interface SearchContextState {
  searchId?: string;
  destinationName?: string;
  destinationId?: string;
  checkIn?: string;
  checkOut?: string;
  filters: SearchFilters;
  sort: SearchSort;
  /** Modalità sort UI (smart / price asc/desc / stars desc). */
  sortMode: SortMode;
  /** Vista risultati corrente (list / map / split). */
  view: ViewMode;
  bounds?: Bounds;
  page: number;
  selectedHotelId?: string;
  dedupSkipped?: boolean;
  supplierErrors?: number;
  /**
   * Risultati cached lato FE per evitare la doppia chiamata `/search` →
   * `/search/{id}/results` al mount di /results. Popolato da SmartHome
   * subito dopo POST /search (la response include già `hotels`).
   * Aggiornato da ResultsClient quando l'utente cambia filtri/sort/page.
   * Resettato a /` o quando l'utente torna alla home.
   */
  hotels?: UnifiedHotelResponse[];
  totalHotels?: number;
  totalUnfiltered?: number;
}

const initialState: SearchContextState = {
  filters: {},
  sort: { field: "price", order: "asc" },
  sortMode: "smart",
  view: "split",
  page: 1,
};

const searchContextSlice = createSlice({
  name: "searchContext",
  initialState,
  reducers: {
    setSearchContext(state, action: PayloadAction<Partial<SearchContextState>>) {
      Object.assign(state, action.payload);
    },
    setFilters(state, action: PayloadAction<SearchFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    setSort(state, action: PayloadAction<SearchSort>) {
      state.sort = action.payload;
    },
    setSortMode(state, action: PayloadAction<SortMode>) {
      state.sortMode = action.payload;
    },
    setView(state, action: PayloadAction<ViewMode>) {
      state.view = action.payload;
    },
    setBounds(state, action: PayloadAction<Bounds | undefined>) {
      state.bounds = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setSelectedHotel(state, action: PayloadAction<string | undefined>) {
      state.selectedHotelId = action.payload;
    },
    /**
     * Salva la SearchResponse del BE (sia da POST /search che da
     * POST /search/{id}/results — hanno la stessa shape). Permette al
     * ResultsClient di renderizzare senza un secondo network roundtrip.
     *
     * Side-effect: quando arriva un searchId DIVERSO dal precedente
     * (= nuova search lanciata da SearchBar/SmartHome), resetta
     * `selectedHotelId`. Senza questo reset, l'HotelDetailDrawer si
     * apre automaticamente su una nuova SERP se l'hotel cliccato nella
     * search precedente è ancora presente nei risultati (es. SEVEN HILLS
     * VILLAGE è sempre in Roma → click → booking → nuova search Roma →
     * drawer si riapre da solo). Il reset locale a /results (stesso
     * searchId) è invece preservato — il drawer aperto resta aperto
     * durante filtri/sort/page change.
     */
    setSearchResults(
      state,
      action: PayloadAction<{
        searchId: string;
        hotels: UnifiedHotelResponse[];
        totalHotels: number;
        totalUnfiltered?: number;
        dedupSkipped?: boolean;
        supplierErrors?: number;
      }>,
    ) {
      const isNewSearch = state.searchId !== action.payload.searchId;
      state.searchId = action.payload.searchId;
      state.hotels = action.payload.hotels;
      state.totalHotels = action.payload.totalHotels;
      state.totalUnfiltered = action.payload.totalUnfiltered;
      state.dedupSkipped = action.payload.dedupSkipped;
      state.supplierErrors = action.payload.supplierErrors;
      if (isNewSearch) {
        state.selectedHotelId = undefined;
      }
    },
    resetSearchContext() {
      return initialState;
    },
  },
});

export const {
  setSearchContext,
  setFilters,
  setSort,
  setSortMode,
  setView,
  setBounds,
  setPage,
  setSelectedHotel,
  setSearchResults,
  resetSearchContext,
} = searchContextSlice.actions;

export default searchContextSlice.reducer;
